// ─────────────────────────────────────────────────────────────────────────────
// TrackingMap.jsx  —  SVG live GPS tracking map
//
// Pure SVG — no external map library needed.
// Renders the route line, completed path, animated truck, and optional
// highlighted driver pin (used by the Map Popup modal).
//
// WHAT TO EDIT HERE:
//   Route waypoints     → edit GPS_ROUTE in constants.js
//   Map region labels   → find the labels array inside the SVG below
//   Map background color → change fill="#e8efe8" on the first <rect>
//   Grid lines          → find the .map(f => <line ... />) blocks
//   Animated truck color → find fill="#ea580c" on the moving circle
//   Pulse ring speed    → change dur="1.5s" on the <animate> tags
//
// PROPS:
//   simIdx     {number}   current segment index (0 to GPS_ROUTE.length-2)
//   simProg    {number}   progress within segment (0.0 to 1.0)
//   simRunning {boolean}  whether animation pulse is active
//   hlDriver   {object|null}  driver to highlight with purple pin { lat, lng, avatar }
// ─────────────────────────────────────────────────────────────────────────────

import { GPS_ROUTE, STEPS_TOTAL, lerp } from "./constants";

export default function TrackingMap({ simIdx, simProg, simRunning, hlDriver }) {
  const W = 600, H = 300;
  const minLat = 33.0, maxLat = 36.5, minLng = -120.5, maxLng = -111.0;

  const tx = lng => ((lng - minLng) / (maxLng - minLng)) * W;
  const ty = lat => (1 - (lat - minLat) / (maxLat - minLat)) * H;

  const c    = GPS_ROUTE[Math.min(simIdx, GPS_ROUTE.length - 1)];
  const n    = GPS_ROUTE[Math.min(simIdx + 1, GPS_ROUTE.length - 1)];
  const cLat = lerp(c.lat, n.lat, simProg);
  const cLng = lerp(c.lng, n.lng, simProg);

  const done    = Math.min(simIdx * 20 + Math.round(simProg * 20), STEPS_TOTAL);
  const di      = Math.floor(done / 20);
  const dp      = (done % 20) / 20;
  const donePts = GPS_ROUTE.slice(0, di + 1).map(p => `${tx(p.lng).toFixed(1)},${ty(p.lat).toFixed(1)}`);
  if (di < GPS_ROUTE.length - 1) {
    const cc = GPS_ROUTE[di], nn = GPS_ROUTE[di + 1];
    donePts.push(`${tx(lerp(cc.lng, nn.lng, dp)).toFixed(1)},${ty(lerp(cc.lat, nn.lat, dp)).toFixed(1)}`);
  }

  const routePts = GPS_ROUTE.map(p => `${tx(p.lng).toFixed(1)},${ty(p.lat).toFixed(1)}`).join(" ");
  const mx = parseFloat(tx(cLng).toFixed(1));
  const my = parseFloat(ty(cLat).toFixed(1));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full">
      {/* Map background — change fill to change map color */}
      <rect width={W} height={H} fill="#e8efe8"/>

      {/* Grid lines */}
      {[.25, .5, .75].map(f => <line key={`h${f}`} x1={0} y1={H*f} x2={W} y2={H*f} stroke="#cdd9bc" strokeWidth="1"/>)}
      {[.17, .33, .5, .67, .83].map(f => <line key={`v${f}`} x1={W*f} y1={0} x2={W*f} y2={H} stroke="#cdd9bc" strokeWidth="1"/>)}

      {/* Terrain blobs */}
      <ellipse cx={140} cy={70}  rx={55} ry={22} fill="#c5d5a8" opacity=".5"/>
      <ellipse cx={400} cy={180} rx={70} ry={26} fill="#cddaae" opacity=".4"/>
      <ellipse cx={500} cy={90}  rx={45} ry={18} fill="#c5d5a8" opacity=".3"/>

      {/* Map region labels — edit x/y/text here to change geography */}
      {[
        {t:"PATHUM THANI",  x:370, y:42,  s:8},
        {t:"NONTHABURI",    x:210, y:125, s:8},
        {t:"Bangkok",       x:270, y:188, s:14, w:"600"},
        {t:"กรุงเทพมหานคร",  x:265, y:205, s:9},
        {t:"SAMUT SAKHON",  x:115, y:268, s:7},
        {t:"SAMUT PRAKAN",  x:380, y:278, s:7},
        {t:"CHACHOENGSAO",  x:480, y:215, s:7},
      ].map(l => (
        <text key={l.t} x={l.x} y={l.y} fontSize={l.s} fontWeight={l.w || "400"}
          fill={l.w ? "#111" : "#6b7280"} fontFamily="system-ui" textAnchor="middle">
          {l.t}
        </text>
      ))}

      {/* Full route — dashed green line */}
      <polyline points={routePts} fill="none" stroke="#16a34a" strokeWidth="2.5" strokeDasharray="5 3" opacity=".55"/>

      {/* Completed path — solid line */}
      {donePts.length > 1 && (
        <polyline points={donePts.join(" ")} fill="none" stroke="#16a34a" strokeWidth="3.5"/>
      )}

      {/* Intermediate waypoint dots */}
      {GPS_ROUTE.slice(1, -1).map((p, i) => (
        <circle key={i} cx={tx(p.lng).toFixed(1)} cy={ty(p.lat).toFixed(1)} r="4" fill="#9ca3af" stroke="#fff" strokeWidth="1.5"/>
      ))}

      {/* Origin marker */}
      <circle cx={tx(GPS_ROUTE[0].lng).toFixed(1)} cy={ty(GPS_ROUTE[0].lat).toFixed(1)} r="13" fill="#1d4ed8" stroke="#fff" strokeWidth="2"/>
      <text x={tx(GPS_ROUTE[0].lng).toFixed(1)} y={parseFloat(ty(GPS_ROUTE[0].lat))+5} textAnchor="middle" fontSize="11" fill="#fff">🚛</text>

      {/* Destination marker */}
      <circle cx={tx(GPS_ROUTE[GPS_ROUTE.length-1].lng).toFixed(1)} cy={ty(GPS_ROUTE[GPS_ROUTE.length-1].lat).toFixed(1)} r="13" fill="#ea580c" stroke="#fff" strokeWidth="2"/>
      <text x={tx(GPS_ROUTE[GPS_ROUTE.length-1].lng).toFixed(1)} y={parseFloat(ty(GPS_ROUTE[GPS_ROUTE.length-1].lat))+5} textAnchor="middle" fontSize="11" fill="#fff">🚛</text>

      {/* Static meeting point pin */}
      <circle cx={250} cy={190} r="14" fill="#7c3aed" stroke="#fff" strokeWidth="2"/>
      <text x={250} y={195} textAnchor="middle" fontSize="10" fill="#fff">📍</text>

      {/* Animated moving truck — changes position as sim runs */}
      <circle cx={mx} cy={my} r="14" fill="#ea580c" stroke="#fff" strokeWidth="2.5"/>
      <text x={mx} y={my+5} textAnchor="middle" fontSize="10" fill="#fff">🚛</text>

      {/* Pulse ring — only visible when simRunning is true */}
      {simRunning && (
        <circle cx={mx} cy={my} r="20" fill="none" stroke="#ea580c" strokeWidth="2" opacity=".4">
          <animate attributeName="r"       values="14;26;14" dur="1.5s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values=".5;0;.5"  dur="1.5s" repeatCount="indefinite"/>
        </circle>
      )}

      {/* Highlighted driver pin (shown via GPS Recall → View on Map) */}
      {hlDriver && (
        <>
          <circle cx={tx(hlDriver.lng).toFixed(1)} cy={ty(hlDriver.lat).toFixed(1)} r="12" fill="#a855f7" stroke="#fff" strokeWidth="2"/>
          <text x={tx(hlDriver.lng).toFixed(1)} y={parseFloat(ty(hlDriver.lat))+5} textAnchor="middle" fontSize="9" fill="#fff">
            {hlDriver.avatar}
          </text>
        </>
      )}

      {/* Zoom controls — decorative; wire to real zoom state if needed */}
      <rect x={W-36} y={8}  width="26" height="24" rx="4" fill="#fff" stroke="#e2e8f0" strokeWidth="1"/>
      <text x={W-23} y={24} textAnchor="middle" fontSize="16" fill="#374151">+</text>
      <rect x={W-36} y={36} width="26" height="24" rx="4" fill="#fff" stroke="#e2e8f0" strokeWidth="1"/>
      <text x={W-23} y={52} textAnchor="middle" fontSize="16" fill="#374151">−</text>
    </svg>
  );
}
